<template>
    <analytics-stat-card
        :loading="loading"
        :error="Boolean(error)"
        :difference="dataComputed.difference"
        :value="dataComputed.number?.value"
        :color="dataComputed.difference < 0 ? 'bad' : 'good'"
        :direction="dataComputed.difference < 0 ? 'down' : 'up'"
    >
        <template v-slot:title>
            <slot name="title">
                {{ title }}
            </slot>
        </template>
        <template v-slot:subtitle>
            <slot name="subtitle"></slot>
        </template>

        <span>{{ dataComputed.number?.label || '' }}</span>

        <template v-slot:explainHeader>{{ title }}</template>
        <template v-slot:explainContent>
            <p>
                <strong>{{ title }}</strong
                ><br />
                <template v-if="props.messageType === null">
                    Gross Count of Messages sent by Voxie during the selected period.
                </template>
                <template v-else>
                    Gross Count of {{ props.messageType.toUpperCase() }} Messages sent during the selected period.
                </template>
            </p>

            <p>
                <strong>{{ title }} %</strong><br />
                <template v-if="props.messageType === null">
                    Gross Percent of unique Contacts that have been messaged relative to the previous period.
                </template>
                <template v-else>
                    Gross Percent of {{ props.messageType.toUpperCase() }} Messages sent relative to the previous
                    period.
                </template>
            </p>
        </template>
    </analytics-stat-card>
</template>
<script setup>
import AnalyticsStatCard from '../../partial/AnalyticsStatCard.vue';
import { vxFormatCount } from '@voxie/frontend-components';
import { calculateDifference, computeComparisonRange, metricsInputValidator } from '~/utils/analytics';
import { computed, watch } from 'vue';
import { outboundMessagesAnalyticsClient } from '~/services';
import { useVersionedRequest } from '~/composables/useVersionedRequest';

const props = defineProps({
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
    messageType: {
        type: String,
        default: null,
        validator: (messageType) => !messageType || ['sms', 'mms'].includes(messageType),
    },
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
});

const { loading, response, error, fetchData } = useVersionedRequest();

const title = computed(() =>
    props.messageType !== null ? `${props.messageType.toUpperCase()} Messages Sent` : `Messages Sent`
);
const dataComputed = computed(() => {
    const responseData = response.value?.data?.data || [];
    const values = props.messageType
        ? responseData.find((item) => item.message_type == props.messageType)?.values || []
        : responseData.reduce((sum, item) => item.values.map((value, index) => sum[index] + value), [0, 0]);

    if (!values.length) {
        return {
            number: undefined,
            difference: undefined,
        };
    }

    const number = values.length >= 2 ? values[1] : values[0];
    const difference = values.length >= 2 ? calculateDifference(values) : undefined;

    return {
        number: {
            value: number,
            label: vxFormatCount(number),
        },
        difference: difference,
    };
});

watch(
    [() => props.fetchParams],
    () => {
        const params = {
            ...props.fetchParams,
            range: computeComparisonRange(props.fetchParams.range, Boolean(props.groupId), Boolean(props.orgUnitId)),
        };

        fetchData(
            outboundMessagesAnalyticsClient.getTotalMessagesSentByMessageType(
                {
                    teamId: Spark.state.currentTeam.id,
                    orgUnitId: props.orgUnitId,
                    groupId: props.groupId,
                },
                params
            )
        ).catch((e) => {
            console.error(e);
        });
    },
    { immediate: true, deep: true }
);

defineExpose({
    value: computed(() => dataComputed.value.number?.value),
    loading,
});
</script>
